<template>
  <div
    class="text-center"
    v-if="
      hasRoleAccessToAction([UserRoleStatus.ADMIN, UserRoleStatus.OWNER]) &&
      hasFeatureAccess(PlatformFeature.INVENTORY)
    "
  >
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      :disabled="stockFilterDisabled"
      bottom
      offset-y
    >
      <template v-slot:activator="{ props }">
        <v-chip
          :color="hasStockFilter ? 'primary' : 'primary'"
          :disabled="stockFilterDisabled"
          v-bind="stockFilterDisabled ? {} : props"
          label
        >
          {{ $t("inventory.stock") }}
          <span v-if="hasStockFilter">&nbsp;|&nbsp;</span>
          <span v-if="hasStockFilter" class="font-weight-bold">
            <template v-if="currentFilterCondition.lowStock">{{
              $t("inventory.lowStock")
            }}</template>
            <template v-if="currentFilterCondition.outOfStock">{{
              $t("inventory.outOfStock")
            }}</template>
            <template
              v-if="
                (currentFilterCondition.lowStock || currentFilterCondition.outOfStock) &&
                hasStockRange
              "
              >,
            </template>
            <template v-if="hasStockRange">{{ stockMinDisplay }}-{{ stockMaxDisplay }}</template>
          </span>
          <v-btn
            v-if="hasStockFilter"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset()"
          />
        </v-chip>
      </template>

      <v-card min-width="350" class="mt-2">
        <v-list>
          <v-list-item :title="$t('inventory.filterByStock')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-list class="mt-1 mx-6">
          <div class="text-subtitle-1 mb-2">
            {{ $t("inventory.stockWarning") }}
          </div>
          <v-radio-group v-model="stockWarningType" hide-details class="mb-4">
            <v-radio
              value="low"
              :label="$t('inventory.lowStock')"
              @keydown.enter="applyAndCloseFilter"
            ></v-radio>

            <v-radio
              value="out"
              :label="$t('inventory.outOfStock')"
              @keydown.enter="applyAndCloseFilter"
            ></v-radio>

            <v-radio
              value="none"
              :label="$t('inventory.noWarning')"
              @keydown.enter="applyAndCloseFilter"
            ></v-radio>
          </v-radio-group>

          <v-divider class="mb-4"></v-divider>

          <div class="d-flex align-center mb-2">
            <div class="text-subtitle-1">{{ $t("inventory.stockRange") }}</div>
            <v-tooltip v-if="outOfStock" location="top">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="small" color="grey" class="ml-2">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ $t("inventory.stockRangeDisabledForOutOfStock") }}</span>
            </v-tooltip>
          </div>

          <div class="d-flex">
            <v-text-field
              v-model.number="stockMin"
              type="number"
              :label="$t('inventory.min')"
              :error-messages="minErrorMessage"
              class="mr-2"
              min="0"
              density="compact"
              :disabled="outOfStock"
              @keydown.enter="applyAndCloseFilter"
            ></v-text-field>

            <v-text-field
              v-model.number="stockMax"
              type="number"
              :label="$t('inventory.max')"
              :error-messages="maxErrorMessage"
              min="0"
              density="compact"
              :disabled="outOfStock"
              @keydown.enter="applyAndCloseFilter"
            ></v-text-field>
          </div>
        </v-list>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="applyAndCloseFilter" color="primary" class="mr-2" :disabled="!isValid">
            {{ $t("page.payment.filter.applyFilter") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import { useProductsStore } from "@/catalog/stores/products";
import i18n from "@/app/plugins/i18n";
import { hasRoleAccessToAction } from "@/shared/utils/role.utils";
import { UserRoleStatus } from "@/shared/enums/status.enum";
import { hasFeatureAccess } from "@/shared/utils/feature.utils";
import { PlatformFeature } from "@/shared/enums/feature.enum";
import { useAccountStore } from "@/account/stores/account";

// Define props to receive the context from parent components
const props = defineProps({
  filterContext: {
    type: String,
    default: "",
  },
});

const menu = ref(false);
const productsStore = useProductsStore();
const { setFilterProductCondition } = productsStore;
const accountStore = useAccountStore();

// Initialize form values when menu is opened
watch(
  () => menu.value,
  (isOpen) => {
    if (isOpen) {
      // Force a fresh initialization from the current filter condition
      initializeFromFilters();
    }
  },
);

// Context property with default value from props
const context = ref(props.filterContext);

// Watch for changes to the filterContext prop
watch(
  () => props.filterContext,
  (newContext) => {
    context.value = newContext;
    applyContextFilters();
  },
);

// Watch for changes to the filterProductCondition directly
watch(
  () => productsStore.filterProductCondition,
  () => {
    initializeFromFilters();
  },
  { deep: true },
);

// Apply the initial context filter on mount
onMounted(() => {
  // First apply context filters if needed
  applyContextFilters(true);

  // Then initialize from the current filter condition
  initializeFromFilters();
});

// Apply filters based on the current context
const applyContextFilters = (init = false) => {
  if (context.value === "inventory") {
    if (init) {
      // Reset filter condition directly since there's no reset method in the products store
      const emptyFilter = {};
      setFilterProductCondition(emptyFilter);
    }

    const filterProductCondition = productsStore.filterProductCondition;
    filterProductCondition.hasInventory = true;
    setFilterProductCondition(filterProductCondition);

    // Only search if there's a context change
    if (context.value) {
      productsStore.searchProducts({}, false);
    }
  }
};

// Form values
const stockWarningType = ref("none");
const stockMin = ref<number | null>(null);
const stockMax = ref<number | null>(null);

// Computed properties for the actual filter values
const lowStock = computed(() => stockWarningType.value === "low");
const outOfStock = computed(() => stockWarningType.value === "out");

// Validation
const minErrorMessage = computed(() => {
  if (stockMin.value === null) return "";
  if (stockMin.value < 0) return i18n.global.t("inventory.negativeValueError");
  if (stockMax.value !== null && stockMin.value > stockMax.value) {
    return i18n.global.t("inventory.minGreaterThanMaxError");
  }
  return "";
});

const maxErrorMessage = computed(() => {
  if (stockMax.value === null) return "";
  if (stockMax.value < 0) return i18n.global.t("inventory.negativeValueError");
  return "";
});

// Check if the form is valid for submission
const isValid = computed(() => {
  // If out of stock is selected, that's valid on its own
  if (stockWarningType.value === "out") {
    return true;
  }

  // If low stock is selected, that's valid on its own
  if (stockWarningType.value === "low") {
    return true;
  }

  // If using stock range, validate it
  if (stockMin.value !== null || stockMax.value !== null) {
    // Both min and max are provided
    if (stockMin.value !== null && stockMax.value !== null) {
      return stockMin.value >= 0 && stockMax.value >= 0 && stockMin.value <= stockMax.value;
    }

    // Only min is provided
    if (stockMin.value !== null) {
      return stockMin.value >= 0;
    }

    // Only max is provided
    if (stockMax.value !== null) {
      return stockMax.value >= 0;
    }
  }

  // If nothing is selected, disable the button
  return false;
});

// Computed properties for filter status display
const isLowStock = computed(() => productsStore.filterProductCondition.lowStock === true);

const isOutOfStock = computed(() => productsStore.filterProductCondition.outOfStock === true);

const hasStockRange = computed(
  () =>
    productsStore.filterProductCondition.stockMin !== undefined ||
    productsStore.filterProductCondition.stockMax !== undefined,
);

const hasStockFilter = computed(
  () => isLowStock.value || isOutOfStock.value || hasStockRange.value,
);

const locationBasedInventoryEnabled = computed(() => {
  return (
    accountStore.account?.accountSettings?.inventory?.location_based_inventory_enabled === true
  );
});

const isGlobalSelected = computed(() => {
  const condition = productsStore.filterProductCondition;
  return "locationId" in condition && condition.locationId === null;
});

const stockFilterDisabled = computed(
  () => locationBasedInventoryEnabled.value && isGlobalSelected.value,
);

// Initialize form values from current filters
const initializeFromFilters = () => {
  const filters = productsStore.filterProductCondition;

  // In case both lowStock and outOfStock are somehow set (shouldn't happen, but for safety),
  // prioritize lowStock since that's the first option in the UI
  if (filters.lowStock === true) {
    stockWarningType.value = "low";
  } else if (filters.outOfStock === true) {
    stockWarningType.value = "out";
  } else {
    stockWarningType.value = "none";
  }

  // Set stock range values if they exist
  stockMin.value = typeof filters.stockMin === "number" ? filters.stockMin : null;
  stockMax.value = typeof filters.stockMax === "number" ? filters.stockMax : null;
};

// Reset the filter
const reset = async () => {
  stockWarningType.value = "none";
  stockMin.value = null;
  stockMax.value = null;

  const filterProductCondition = productsStore.filterProductCondition;
  delete filterProductCondition.lowStock;
  delete filterProductCondition.outOfStock;
  delete filterProductCondition.stockMin;
  delete filterProductCondition.stockMax;

  // Keep hasInventory property when in inventory context
  if (context.value !== "inventory") {
    delete filterProductCondition.hasInventory;
  } else {
    filterProductCondition.hasInventory = true;
  }

  setFilterProductCondition(filterProductCondition);
  productsStore.searchProducts({}, false);
};

// Apply the filter and close the menu
const applyAndCloseFilter = () => {
  if (!isValid.value) return;

  const filterProductCondition = productsStore.filterProductCondition;

  // Reset previous stock filters
  delete filterProductCondition.lowStock;
  delete filterProductCondition.outOfStock;
  delete filterProductCondition.stockMin;
  delete filterProductCondition.stockMax;

  // Apply selected filters based on radio option
  if (stockWarningType.value === "low") {
    filterProductCondition.lowStock = true;
    filterProductCondition.hasInventory = true;
  } else if (stockWarningType.value === "out") {
    filterProductCondition.outOfStock = true;
    filterProductCondition.hasInventory = true;
  } else {
    // Only delete hasInventory if not in inventory context
    if (context.value !== "inventory") {
      delete filterProductCondition.hasInventory;
    }
  }

  // Apply stock range if not out of stock
  if (stockWarningType.value !== "out") {
    if (stockMin.value !== null) {
      filterProductCondition.stockMin = stockMin.value;
      filterProductCondition.hasInventory = true;
    }

    if (stockMax.value !== null) {
      filterProductCondition.stockMax = stockMax.value;
      filterProductCondition.hasInventory = true;
    }
  }

  // Always include hasInventory when in inventory context
  if (context.value === "inventory") {
    filterProductCondition.hasInventory = true;
  }

  setFilterProductCondition(filterProductCondition);
  productsStore.searchProducts({}, false);
  menu.value = false;
};

// Get current filter condition directly for more reliable display in the chip
const currentFilterCondition = computed(() => productsStore.filterProductCondition);

// Display stock range with better formatting
const stockMinDisplay = computed(() => {
  return currentFilterCondition.value.stockMin !== undefined
    ? currentFilterCondition.value.stockMin
    : "0";
});

const stockMaxDisplay = computed(() => {
  return currentFilterCondition.value.stockMax !== undefined
    ? currentFilterCondition.value.stockMax
    : "∞";
});

watch(
  () => stockFilterDisabled.value,
  (disabled) => {
    if (!disabled) return;
    menu.value = false;
    if (
      hasStockFilter.value ||
      stockWarningType.value !== "none" ||
      stockMin.value !== null ||
      stockMax.value !== null
    ) {
      void reset();
      return;
    }
    stockWarningType.value = "none";
    stockMin.value = null;
    stockMax.value = null;
  },
  { immediate: true },
);
</script>
